/**
 * Date Utility Functions
 * =====================
 * Consistent date formatting across the app.
 * Always uses DD/MM/yyyy format regardless of language setting.
 */

import { format as dateFnsFormat, parse, parseISO } from 'date-fns';

/**
 * Format date as DD/MM/yyyy
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'dd/MM/yyyy');
};

/**
 * Format date as DD/MM/yyyy HH:mm
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date time string
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'dd/MM/yyyy HH:mm');
};

/**
 * Format date for display headers (e.g., "Mon, 15/01")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDateHeader = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'EEE, dd/MM');
};

/**
 * Format date for display with month name (e.g., "15 Jan 2024")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted date string
 */
export const formatDateWithMonth = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'dd MMM yyyy');
};

/**
 * Format date for API requests (yyyy-MM-dd)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} ISO date string
 */
export const formatDateForAPI = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'yyyy-MM-dd');
};

/**
 * Format month and year (e.g., "January 2024")
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted month year string
 */
export const formatMonthYear = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'MMMM yyyy');
};

/**
 * Format time only (HH:mm)
 * @param {Date|string} date - Date object or ISO string
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return dateFnsFormat(dateObj, 'HH:mm');
};
